import { createSignal, createMemo, createEffect, onMount, onCleanup, untrack, For, Index, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useActiveBot } from '../../state/activeBot';
import ItemStackView from '../../components/ItemStackView';
import QuantityInput from '../../components/QuantityInput';
import StorageSelect from '../../components/StorageSelect';
import { EmptyState } from '../../components/EmptyState';
import { SkeletonCard } from '../../components/Skeleton';
import { RelativeTime } from '../../components/RelativeTime';
import BotScoped from '../../components/BotScoped';
import Text from '../../components/Text';
import { fromItemStackDto } from '../../services/itemRefAdapters';
import { stripColorCodes } from '../../services/itemDisplayName';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { formatDateTime } from '../../services/dateFormat';
import { useApi, isOk } from '../../api';
import { firstError } from '../../api/envelope';
import { useToast } from '../../components/MessageBox';
import { showConfirm } from '../../components/Dialog';
import { useTranslation } from '../../hooks/useTranslation';
import type { CaseOpeningAccountDto, OwnedCaseDto } from '../../api/generated/schemas';
import styles from './Demolish.module.css';
import caseStyles from './CaseOpening.module.css';

const OUTCOME_COMPLETED = 1;
const POLL_INTERVAL_MS = 3000;
const TASK_START_GRACE_MS = 30000;

interface CaseChoice {
    selected: boolean;
    amount: number;
}

export default function CaseOpeningPage() {
    return <BotScoped><CaseOpeningInner /></BotScoped>;
}

function CaseOpeningInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const inv = useInventoryApi();
    const { botId: activeBotId } = useActiveBot();
    const botId = () => activeBotId() ?? '';

    const [account, setAccount] = createSignal<CaseOpeningAccountDto | null>(null);
    const [loading, setLoading] = createSignal(true);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [storageId, setStorageId] = createSignal('');
    const [choices, setChoices] = createStore<Record<string, CaseChoice>>({});
    const [submitting, setSubmitting] = createSignal(false);
    const [taskRequestedAt, setTaskRequestedAt] = createSignal<number | null>(null);

    const cases = createMemo(() => account()?.cases ?? []);
    const isRunning = () => account()?.isRunning === true;
    const awaitingTask = () => isRunning() || (taskRequestedAt() != null && Date.now() - taskRequestedAt()! < TASK_START_GRACE_MS);
    const selectedLines = createMemo(() => cases()
        .filter(owned => choices[owned.name]?.selected && owned.count > 0)
        .map(owned => ({ caseName: owned.name, amount: Math.min(choices[owned.name].amount, owned.count) })));
    const selectedTotal = () => selectedLines().reduce((sum, line) => sum + line.amount, 0);

    async function loadAccount(id: string) {
        if (!id) { setAccount(null); setLoading(false); return; }
        try {
            const res = await api.caseOpening.getCaseOpeningAccount(id);
            if (id !== botId()) return;
            if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('CASE_OPENING_LOAD_FAILED')); return; }
            const loaded = res.data.value;
            for (const owned of loaded.cases)
                if (!choices[owned.name]) setChoices(owned.name, { selected: false, amount: owned.count });
            setAccount(loaded);
            if (loaded.isRunning) setTaskRequestedAt(null);
            if (!storageId() && loaded.lastRun) setStorageId(loaded.lastRun.storageId);
        } catch (e: any) {
            toast?.error(e?.message ?? t('CASE_OPENING_LOAD_FAILED'));
        } finally { setLoading(false); }
    }

    onMount(async () => {
        try { setStorages(await inv.fetchStorages()); }
        catch (e: any) { toast?.error(e?.message ?? t('CASE_OPENING_LOAD_FAILED')); }
    });

    createEffect(() => {
        const id = botId();
        untrack(() => {
            setLoading(true);
            setTaskRequestedAt(null);
            setChoices(reconcile({}));
            void loadAccount(id);
        });
    });

    const poll = window.setInterval(() => {
        const requestedAt = taskRequestedAt();
        if (requestedAt != null && Date.now() - requestedAt >= TASK_START_GRACE_MS) setTaskRequestedAt(null);
        if (document.visibilityState !== 'visible' || !awaitingTask()) return;
        void loadAccount(botId());
    }, POLL_INTERVAL_MS);
    onCleanup(() => clearInterval(poll));

    async function scan() {
        const res = await api.caseOpening.scanCases(botId());
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('CASE_OPENING_SCAN_FAILED')); return; }
        setTaskRequestedAt(Date.now());
        toast?.info(t('CASE_OPENING_SCAN_STARTED'));
    }

    async function openSelected() {
        if (!storageId()) { toast?.error(t('CASE_OPENING_SELECT_STORAGE')); return; }
        if (selectedLines().length === 0) { toast?.error(t('CASE_OPENING_NOTHING_SELECTED')); return; }
        setSubmitting(true);
        try {
            const res = await api.caseOpening.openCases(botId(), { storageId: storageId(), lines: selectedLines() });
            if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('CASE_OPENING_OPEN_FAILED')); return; }
            setTaskRequestedAt(Date.now());
            toast?.info(t('CASE_OPENING_OPEN_STARTED', selectedTotal()));
        } finally { setSubmitting(false); }
    }

    async function stop() {
        const confirmed = await showConfirm(t('CASE_OPENING_STOP_CONFIRM'), true);
        if (!confirmed) return;
        const res = await api.caseOpening.stopCaseOpening(botId());
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('CASE_OPENING_STOP_FAILED')); return; }
        toast?.info(t('CASE_OPENING_STOPPED'));
        await loadAccount(botId());
    }

    function caseLabel(caseName: string) {
        return cases().find(owned => owned.name === caseName)?.label ?? stripColorCodes(caseName);
    }

    // QuantityInput reports its value as it mounts, which can be before this row's entry exists.
    function updateChoice(owned: OwnedCaseDto, patch: Partial<CaseChoice>) {
        setChoices(owned.name, (choice: CaseChoice | undefined) => ({ ...(choice ?? { selected: false, amount: owned.count }), ...patch }));
    }

    function CaseRow(props: { owned: OwnedCaseDto }) {
        const choice = () => choices[props.owned.name] ?? { selected: false, amount: props.owned.count };
        const empty = () => props.owned.count === 0;
        return (
            <div class={`${caseStyles.caseRow} ${choice().selected ? caseStyles.caseRowSelected : ''}`}>
                <label class={caseStyles.casePick}>
                    <input
                        type="checkbox"
                        checked={choice().selected}
                        disabled={empty()}
                        onChange={e => updateChoice(props.owned, { selected: e.currentTarget.checked })}
                    />
                    <ItemStackView stack={fromItemStackDto(props.owned.item)} size={28} bare />
                    <span class={caseStyles.caseName}>{props.owned.label}</span>
                </label>
                <span class={caseStyles.owned}>{t('CASE_OPENING_OWNED', props.owned.count)}</span>
                <div class={caseStyles.amount}>
                    <QuantityInput
                        value={choice().amount}
                        onChange={amount => updateChoice(props.owned, { amount })}
                        min={1}
                        max={Math.max(1, props.owned.count)}
                        presets={false}
                        placeholder={t('CASE_OPENING_AMOUNT_PLACEHOLDER')}
                        inline
                    />
                    <button
                        type="button"
                        class="btn btn-secondary btn-sm"
                        disabled={empty()}
                        title={t('CASE_OPENING_MAX_TITLE', props.owned.count)}
                        onClick={() => updateChoice(props.owned, { selected: true, amount: props.owned.count })}
                    >
                        <Text val="CASE_OPENING_MAX" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="CASE_OPENING_TITLE" /></h1>
                <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                    <Show when={isRunning()}>
                        <button class="btn btn-danger" onClick={stop} disabled={!botId()}>
                            <i class="fa-solid fa-stop" /> <Text val="CASE_OPENING_STOP" />
                        </button>
                    </Show>
                    <button class="btn btn-secondary" onClick={scan} disabled={!botId() || awaitingTask()}>
                        <i class="fa-solid fa-magnifying-glass" /> <Text val="CASE_OPENING_SCAN" />
                    </button>
                </div>
            </div>
            <p class={styles.subtitle}><Text val="CASE_OPENING_SUBTITLE" /></p>

            <Show when={!loading()} fallback={<div class="v" style={{ gap: 'var(--spacing-md)' }}><SkeletonCard /><SkeletonCard /></div>}>
                <Show when={account()?.scannedAt} fallback={
                    <EmptyState
                        icon="fa-box-open"
                        title={t('CASE_OPENING_NOT_SCANNED')}
                        hint={t('CASE_OPENING_NOT_SCANNED_HINT')}
                        action={
                            <button class="btn btn-primary" onClick={scan} disabled={!botId() || awaitingTask()}>
                                <i class="fa-solid fa-magnifying-glass" /> <Text val="CASE_OPENING_SCAN" />
                            </button>
                        }
                    />
                }>
                    <div class={`fcard v ${caseStyles.panel}`}>
                        <div class={caseStyles.panelHeader}>
                            <h2 class={styles.formTitle}><Text val="CASE_OPENING_CASES" /></h2>
                            <span class={caseStyles.muted}>
                                <Text val="CASE_OPENING_SCANNED" /> <RelativeTime value={account()!.scannedAt!} />
                            </span>
                        </div>

                        <Show when={(account()?.unclaimedRewards ?? 0) > 0}>
                            <div class={caseStyles.notice}>
                                <i class="fa-solid fa-gift" /> {t('CASE_OPENING_UNCLAIMED', account()!.unclaimedRewards!)}
                            </div>
                        </Show>

                        <Show when={cases().length > 0} fallback={
                            <EmptyState icon="fa-box-open" title={t('CASE_OPENING_NO_CASES')} hint={t('CASE_OPENING_NO_CASES_HINT')} />
                        }>
                            <div class={caseStyles.caseList}>
                                <Index each={cases()}>{(owned) => <CaseRow owned={owned()} />}</Index>
                            </div>

                            <form class={caseStyles.footer} onSubmit={e => { e.preventDefault(); void openSelected(); }}>
                                <label class={`${styles.field} ${caseStyles.storageField}`}>
                                    <span class={styles.fieldLabel}><Text val="CASE_OPENING_F_STORAGE" /></span>
                                    <StorageSelect
                                        storages={storages()}
                                        value={storageId()}
                                        onChange={setStorageId}
                                        placeholder={t('CASE_OPENING_SELECT_STORAGE_OPTION')}
                                    />
                                </label>
                                <span class={caseStyles.muted}>{t('CASE_OPENING_SELECTED_TOTAL', selectedTotal())}</span>
                                <button
                                    type="submit"
                                    class="btn btn-primary"
                                    disabled={!botId() || submitting() || awaitingTask() || !storageId() || selectedLines().length === 0}
                                >
                                    <Show when={submitting()} fallback={<i class="fa-solid fa-play" />}>
                                        <i class="fa-solid fa-spinner fa-spin" />
                                    </Show>
                                    {' '}<Text val="CASE_OPENING_OPEN" />
                                </button>
                            </form>
                        </Show>
                    </div>
                </Show>

                <Show when={account()?.lastRun}>
                    {(run) => (
                        <div class={`fcard v ${caseStyles.panel}`}>
                            <div class={caseStyles.panelHeader}>
                                <h2 class={styles.formTitle}><Text val="CASE_OPENING_LAST_RUN" /></h2>
                                <span class={caseStyles.muted}>
                                    {formatDateTime(run().startedAt)}
                                    <Show when={!run().finishedAt}>{' · '}<Text val="CASE_OPENING_LAST_RUN_RUNNING" /></Show>
                                </span>
                            </div>
                            <For each={run().lines}>
                                {(line) => (
                                    <div class={caseStyles.runRow}>
                                        <span>{caseLabel(line.caseName)} {line.opened}/{line.requested}</span>
                                        <span class={line.outcome === OUTCOME_COMPLETED ? caseStyles.muted : caseStyles.problem}>
                                            {t(`CASE_OPENING_OUTCOME_${line.outcome}`)}
                                        </span>
                                    </div>
                                )}
                            </For>
                            <Show when={run().rewards.length > 0}>
                                <div class={caseStyles.prizes}>
                                    <span class={caseStyles.muted}><Text val="CASE_OPENING_PRIZES" /></span>
                                    <For each={run().rewards}>
                                        {(prize) => <span class="minicard">{prize.count} × {prize.text}</span>}
                                    </For>
                                </div>
                            </Show>
                            <Show when={run().depositFailed}>
                                <div class={caseStyles.problem}><Text val="CASE_OPENING_DEPOSIT_FAILED" /></div>
                            </Show>
                        </div>
                    )}
                </Show>
            </Show>
        </div>
    );
}
